import type { CaptionStyle } from "@teksta/shared-types";

type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

export type WaveformPresetId = "reactive-mirrored";

export interface AudioWaveformConfig {
  enabled: boolean;
  preset: WaveformPresetId;
  color?: string;
  barCount?: number;
}

export interface WaveformBarsStyle {
  preset: WaveformPresetId;
  color: string;
  glowColor: string;
  barCount: number;
}

/**
 * Number of linear-frequency samples we ask the source spectrum for before
 * collapsing it onto the bars with {@link spectrumToBars}. The export path
 * ({@link visualizeAudio}) requires a power of two, and a higher resolution than
 * the bar count gives the logarithmic mapping enough buckets to spread across.
 */
export const WAVEFORM_SPECTRUM_SAMPLES = 256;

export const AUDIO_WAVEFORM_PRESETS: Record<
  WaveformPresetId,
  Pick<WaveformBarsStyle, "preset" | "barCount">
> = {
  "reactive-mirrored": {
    preset: "reactive-mirrored",
    barCount: 48,
  },
};

const DEFAULT_PRESET: WaveformPresetId = "reactive-mirrored";

export const DEFAULT_AUDIO_WAVEFORM: AudioWaveformConfig = {
  enabled: true,
  preset: DEFAULT_PRESET,
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));

export function waveformStyleFromCaption(
  captionStyle: CaptionStyle,
  preset: WaveformPresetId = DEFAULT_PRESET,
  overrides: { color?: string; barCount?: number } = {}
): WaveformBarsStyle {
  const base = AUDIO_WAVEFORM_PRESETS[preset] ?? AUDIO_WAVEFORM_PRESETS[DEFAULT_PRESET];
  const color = overrides.color || captionStyle.highlight.color || "#34d399";
  return {
    ...base,
    color,
    glowColor: color,
    barCount: Math.max(1, Math.floor(overrides.barCount ?? base.barCount)),
  };
}

export function waveformStyleFromConfig(
  captionStyle: CaptionStyle,
  config: AudioWaveformConfig
): WaveformBarsStyle {
  return waveformStyleFromCaption(captionStyle, config.preset, {
    color: config.color,
    barCount: config.barCount,
  });
}

/**
 * Collapse a linear-frequency spectrum (index 0 = DC … last = Nyquist) onto
 * `barCount` **logarithmically** spaced bars.
 *
 * A linear slice piles almost all musical energy into the first few buckets (bass
 * dominates and the FFT bins are linear in Hz), which is why the old mapping only
 * lit up the left edge. Mapping bar boundaries exponentially across the spectrum
 * gives each bar a roughly equal share of perceived pitch, so the bars stay lively
 * across the whole width.
 *
 * `maxValue` normalises the input range — pass 255 for `getByteFrequencyData`
 * bytes, 1 for the already-normalised floats from Remotion's `visualizeAudio`.
 * Both the preview and the export funnel through this so they match.
 */
export function spectrumToBars(
  spectrum: ArrayLike<number>,
  barCount: number,
  maxValue = 1
): number[] {
  const count = Math.max(1, Math.floor(barCount));
  const len = spectrum.length;
  if (len === 0) return Array.from({ length: count }, () => 0);
  const minBin = Math.max(1, Math.min(1, len - 1));
  const ratio = len / minBin;
  return Array.from({ length: count }, (_, index) => {
    const start = Math.floor(minBin * ratio ** (index / count));
    const end = Math.max(start + 1, Math.floor(minBin * ratio ** ((index + 1) / count)));
    let sum = 0;
    let n = 0;
    for (let sourceIndex = start; sourceIndex < end && sourceIndex < len; sourceIndex += 1) {
      sum += spectrum[sourceIndex] ?? 0;
      n += 1;
    }
    return clamp01(n > 0 ? sum / n / maxValue : 0);
  });
}

function roundRect(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function fillRoundedBar(
  ctx: Ctx2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  roundRect(ctx, x, y, width, height, radius);
  ctx.fill();
}

/**
 * Draw a frequency spectrum mirrored about the horizontal centre: bass sits in the
 * middle and higher frequencies fan out symmetrically to both edges — the Winamp /
 * "butterfly" look. Each bar is also mirrored vertically about the midline like a
 * graphic equaliser. `bins` runs bass→treble (length = `style.barCount`); we render
 * `2 × bins.length` visual bars.
 */
export function drawWaveformBars(
  ctx: Ctx2D,
  bins: ArrayLike<number>,
  style: WaveformBarsStyle,
  w: number,
  h: number
) {
  if (w <= 0 || h <= 0 || bins.length === 0) return;

  ctx.save();
  ctx.fillStyle = "#050608";
  ctx.fillRect(0, 0, w, h);

  const count = bins.length;
  const totalBars = count * 2;
  const areaWidth = w * 0.9;
  const slot = areaWidth / totalBars;
  const gap = Math.max(2, slot * 0.32);
  const barWidth = Math.max(2, slot - gap);
  const radius = Math.max(2, barWidth * 0.5);
  const centerX = w / 2;
  const mid = h * 0.5;
  const maxHalfHeight = h * 0.34;
  const innerGap = h * 0.012;

  ctx.fillStyle = style.color;
  ctx.shadowColor = style.glowColor;
  ctx.shadowBlur = h * 0.02;

  for (let index = 0; index < count; index += 1) {
    const amplitude = Math.max(0.03, clamp01(bins[index] ?? 0) ** 0.7);
    const halfHeight = amplitude * maxHalfHeight;
    const rightX = centerX + index * slot + gap / 2;
    const leftX = centerX - (index + 1) * slot + gap / 2;
    for (const x of [rightX, leftX]) {
      fillRoundedBar(ctx, x, mid - innerGap - halfHeight, barWidth, halfHeight, radius);
      fillRoundedBar(ctx, x, mid + innerGap, barWidth, halfHeight, radius);
    }
  }
  ctx.restore();
}
